import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  HeightProps,
  SpaceProps,
} from 'styled-system';
import {
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  height,
  minHeight,
  minWidth,
  space,
  themeGet,
} from 'styled-system';

import h24LogoSvg from 'common/assets/images/home-24-logo.svg';
import { Button } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { SubtitleST3 } from 'common/components/Typography';
import { useIsObserved } from 'common/hooks/intersection-observer';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { StrokeProps } from 'common/theme/system-types';
import type {
  AsProps,
  BoxSizingProps,
  CursorProps,
  RotateProps,
  TextDecorationProps,
  ThemeMinHeightProps,
  WordBreakProps,
} from 'common/theme/system-utilities';
import {
  cursor,
  rotate,
  stroke,
  textDecoration,
  themeMinHeight,
  wordBreak,
} from 'common/theme/system-utilities';
import type { Filter } from 'common/types/SortAndFilter';
import { countSelectedValuesInFilter } from 'common/utils/filterGroups';
import { getFilterIcon } from './utils';

interface PromotedFilterChipProps extends AsProps {
  filter: Filter & { groupCount?: number };
  showIcon?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  showCount?: boolean;
  onPromotedFilterViewed: () => void;
  filterKey?: string;
}

const ICON_SIZE = 24;

const FilterButton = styled(Button)<BorderRadiusProps & TextDecorationProps>`
  ${borderRadius};
  ${textDecoration};
`;

const filterButtonStyles: ComponentProps<typeof FilterButton> = {
  borderRadius: 'x-large',
  textDecoration: 'none',
};

const FilterChipWrapper = styled(Flex)<
  { isSelected: boolean } & ThemeMinHeightProps &
    BorderStyleProps &
    BorderWidthProps &
    BorderColorProps &
    BoxSizingProps
>`
  ${display};
  ${themeMinHeight};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};

  ${props =>
    props.isSelected &&
    css`
      border-color: ${themeGet('colors.sys.primary.text.default')(props)};
      border-width: ${themeGet('borderWidths.small')(props)};
      outline: ${themeGet('borderWidths.small')(props)} solid
        ${themeGet('colors.sys.primary.text.default')(props)};
      outline-offset: -2px;
    `}
`;

const filterChipWrapperStyles: ComponentProps<typeof FilterChipWrapper> = {
  isSelected: false,
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  maxWidth: '280px',
  borderRadius: 'x-large',
  mb: 'sp_0',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.weak',
  backgroundColor: 'sys.neutral.background.default',
  pl: 'sp_12',
  boxSizing: 'border-box',
};

const FilterText = styled(SubtitleST3)<WordBreakProps>`
  ${wordBreak};
`;

const filterTextStyles: ComponentProps<typeof FilterText> = {
  as: 'span',
  wordBreak: 'break-all',
  whiteSpace: 'nowrap',
};

export const ButtonHitArea = styled.li<SpaceProps & CursorProps>`
  ${space};
  ${cursor};
  -webkit-tap-highlight-color: transparent;

  &:first-of-type {
    padding-left: 4px;
  }
`;

export const buttonHitAreaStyles: ComponentProps<typeof ButtonHitArea> = {
  p: 'sp_4',
  py: 'sp_8',
  cursor: 'pointer',
};

const ArrowRight = styled(Icon)<StrokeProps & RotateProps>`
  ${minWidth};
  ${minHeight};
  ${rotate};
  ${stroke};
`;

const arrowStyles = {
  minWidth: `${ICON_SIZE}`,
  minHeight: `${ICON_SIZE}`,
  size: `${ICON_SIZE}px`,
  rotate: '-90',
};

const Home24Logo = styled.img<HeightProps>`
  ${height};
`;

const useIsSelected = (filter: Filter & { groupCount?: number }) => {
  if (filter.groupCount) {
    return filter.groupCount;
  }

  return countSelectedValuesInFilter(filter);
};

// eslint-disable-next-line complexity
const PromotedFilterChip = ({
  filter,
  showIcon,
  onClick,
  as = 'button',
  href,
  showCount = true,
  onPromotedFilterViewed,
  filterKey,
}: PromotedFilterChipProps) => {
  const HOME_24_FILTER_KEY = 'isHome24';
  const count = useIsSelected(filter);
  const { t } = useLocalization();
  const isSelected = count >= 1;
  const [ref, isObserved] = useIsObserved();

  const filterIcon = getFilterIcon(filterKey);

  useEffect(() => {
    if (isObserved) {
      onPromotedFilterViewed();
    }
  }, [isObserved]);

  const ariaLabel =
    t('h24_filter_by') +
    `: ${filter.name} ${count && showCount ? `${count} ${t('h24_selected_filters')}` : ''}`;

  return (
    <ButtonHitArea {...buttonHitAreaStyles} ref={ref}>
      <FilterButton
        {...filterButtonStyles}
        onClick={onClick}
        kind="bare"
        aria-label={ariaLabel}
        as={as}
        {...(href ? { href } : {})}
        {...(as === 'button' ? { 'aria-pressed': isSelected } : { role: 'link' })}
      >
        <FilterChipWrapper
          {...filterChipWrapperStyles}
          isSelected={isSelected}
          pr={showIcon ? 'sp_0' : 'sp_12'}
          data-testid="promoted-filter-chip"
        >
          {filterKey === HOME_24_FILTER_KEY ? (
            <Home24Logo src={h24LogoSvg} height={20} alt="" />
          ) : (
            <FilterText
              color={isSelected ? 'sys.primary.text.default' : 'sys.neutral.text.default'}
              {...filterTextStyles}
            >
              <Flex alignItems="center" gap={filterIcon?.gap || 'sp_0'}>
                {filterIcon && (
                  <Icon
                    name={filterIcon.name}
                    size={filterIcon.size}
                    color={isSelected ? 'sys.primary.text.default' : 'sys.neutral.text.default'}
                    fill={isSelected ? 'sys.primary.text.default' : 'sys.neutral.text.default'}
                  />
                )}
                {`${filter.name} ${count && showCount ? `(${count})` : ''}`}
              </Flex>
            </FilterText>
          )}
          {showIcon && (
            <Flex data-testid="filter-chip-button" tabIndex={-1} mr="sp_4">
              <ArrowRight
                {...arrowStyles}
                stroke={isSelected ? 'sys.primary.background.default' : 'sys.neutral.text.default'}
                name="arrowLightDown"
                ariaLabel=""
              />
            </Flex>
          )}
        </FilterChipWrapper>
      </FilterButton>
    </ButtonHitArea>
  );
};

export default PromotedFilterChip;
